import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "../config/api";

type GroupKey = "POT" | "FLOWER" | "PACKAGE";

type ProductSearchItem = {
  code: string;
  name: string;
  price: number;
  unit: string | null;
};

type ScanSpecialItem = {
  id: number;
  product_code: string;
  group_key: GroupKey;
  sort_order: number;
  is_active: boolean;
  product: ProductSearchItem;
};

type ScanSpecialItemsResponse = {
  ok?: boolean;
  items?: unknown;
  item?: unknown;
  error?: string;
};

type ProductsResponse = {
  items?: unknown;
  error?: string;
};

const TOKEN_STORAGE_KEY = "bg_shop_token";
const SCAN_SPECIAL_ITEMS_API = apiUrl("/scan-special-items");

function toProductSearchItem(row: unknown): ProductSearchItem | null {
  if (!row || typeof row !== "object") return null;
  const data = row as Record<string, unknown>;
  const code = typeof data.code === "string" ? data.code.trim() : "";
  const name = typeof data.name === "string" ? data.name.trim() : "";
  const price = Number(data.price ?? 0);
  if (!code || !name || !Number.isFinite(price)) return null;

  return {
    code,
    name,
    price,
    unit: typeof data.unit === "string" && data.unit.trim() ? data.unit.trim() : null,
  };
}

function toScanSpecialItem(row: unknown): ScanSpecialItem | null {
  if (!row || typeof row !== "object") return null;
  const data = row as Record<string, unknown>;
  const id = Number(data.id);
  const product_code = typeof data.product_code === "string" ? data.product_code.trim() : "";
  const group_key = typeof data.group_key === "string" ? data.group_key.trim().toUpperCase() : "";
  const sort_order = Number(data.sort_order ?? 0);
  const product = toProductSearchItem(data.product);

  if (!Number.isInteger(id) || id <= 0) return null;
  if (!product_code || !["POT", "FLOWER", "PACKAGE"].includes(group_key) || !product) return null;

  return {
    id,
    product_code,
    group_key: group_key as GroupKey,
    sort_order: Number.isFinite(sort_order) ? sort_order : 0,
    is_active: Boolean(data.is_active),
    product,
  };
}

function getErrorMessage(code: string): string {
  switch (code) {
    case "product_code_required":
      return "กรุณาเลือกสินค้าที่ต้องการเพิ่ม";
    case "invalid_group_key":
      return "หมวดที่เลือกไม่ถูกต้อง";
    case "product_not_found":
      return "ไม่พบสินค้านี้ในระบบ";
    case "scan_special_item_already_exists":
      return "สินค้านี้อยู่ในหมวดนี้แล้ว";
    case "invalid_scan_special_item_id":
      return "รายการที่ต้องการลบไม่ถูกต้อง";
    case "scan_special_item_not_found":
      return "ไม่พบรายการที่ต้องการลบ";
    case "failed_to_fetch_scan_special_items":
      return "โหลดรายการปุ่มลัดหน้าแสกนไม่สำเร็จ";
    case "failed_to_create_scan_special_item":
      return "เพิ่มสินค้าเข้าหมวดไม่สำเร็จ";
    case "failed_to_delete_scan_special_item":
      return "ลบสินค้าออกจากหมวดไม่สำเร็จ";
    case "missing_auth_token":
    case "invalid_auth_token":
      return "กรุณาเข้าสู่ระบบใหม่อีกครั้ง";
    default:
      return "เกิดข้อผิดพลาดระหว่างจัดการรายการปุ่มลัดหน้าแสกน";
  }
}

export default function ScanSpecialItemsPage(): JSX.Element {
  const [items, setItems] = useState<ScanSpecialItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [groupKey, setGroupKey] = useState<GroupKey>("POT");
  const [sortOrder, setSortOrder] = useState<number>(0);
  const [searchText, setSearchText] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<ProductSearchItem[]>([]);

  useEffect(() => {
    void loadItems();
  }, []);

  const grouped = useMemo(
    () => ({
      POT: items.filter((item) => item.group_key === "POT"),
      FLOWER: items.filter((item) => item.group_key === "FLOWER"),
      PACKAGE: items.filter((item) => item.group_key === "PACKAGE"),
    }),
    [items]
  );

  function getGroupLabel(group: GroupKey): string {
    switch (group) {
      case "POT":
        return "กระถาง";
      case "FLOWER":
        return "ดอกไม้";
      case "PACKAGE":
        return "แพ็กเกจ";
      default:
        return group;
    }
  }

  // PMN-CRITICAL(/ScanSpecialItemsPage): This loads master quick-action/package config used by /scan and TikTok package picker; group_key/product shape is contract-critical.
  async function loadItems(): Promise<void> {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
    if (!token) {
      setError("กรุณาเข้าสู่ระบบใหม่อีกครั้ง");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const resp = await fetch(SCAN_SPECIAL_ITEMS_API, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = (await resp.json().catch(() => ({}))) as ScanSpecialItemsResponse;
      if (!resp.ok || !data.ok || !Array.isArray(data.items)) {
        throw new Error(data.error ?? "failed_to_fetch_scan_special_items");
      }

      setItems(data.items.map(toScanSpecialItem).filter((item): item is ScanSpecialItem => item != null));
      setError("");
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_fetch_scan_special_items";
      setError(getErrorMessage(code));
    } finally {
      setLoading(false);
    }
  }

  async function handleSearch(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    const q = searchText.trim();
    if (!q) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const url = new URL(apiUrl("/products"));
      url.searchParams.set("q", q);

      const resp = await fetch(url.toString(), {
        headers: { Accept: "application/json" },
      });
      const data = (await resp.json().catch(() => ({}))) as ProductsResponse;
      if (!resp.ok || !Array.isArray(data.items)) {
        throw new Error(data.error ?? "failed_to_fetch_products");
      }

      setSearchResults(
        data.items.map(toProductSearchItem).filter((item): item is ProductSearchItem => item != null)
      );
    } catch {
      setError("ค้นหาสินค้าไม่สำเร็จ");
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }

  // PMN-CRITICAL(/ScanSpecialItemsPage): Adding PACKAGE/POT/FLOWER changes downstream options; keep group_key values exactly POT, FLOWER, PACKAGE.
  async function handleAdd(product: ProductSearchItem): Promise<void> {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
    if (!token) {
      setError("กรุณาเข้าสู่ระบบใหม่อีกครั้ง");
      return;
    }

    setSaving(true);
    setError("");
    setSuccessMessage("");

    try {
      const resp = await fetch(SCAN_SPECIAL_ITEMS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          product_code: product.code,
          group_key: groupKey,
          sort_order: sortOrder,
          is_active: true,
        }),
      });

      const data = (await resp.json().catch(() => ({}))) as ScanSpecialItemsResponse;
      if (!resp.ok || !data.ok || !data.item) {
        throw new Error(data.error ?? "failed_to_create_scan_special_item");
      }

      const nextItem = toScanSpecialItem(data.item);
      if (!nextItem) {
        throw new Error("failed_to_create_scan_special_item");
      }

      setItems((prev) =>
        [...prev, nextItem].sort((a, b) => {
          if (a.group_key !== b.group_key) return a.group_key.localeCompare(b.group_key);
          if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
          return a.id - b.id;
        })
      );
      setSuccessMessage(`เพิ่ม ${product.name} เข้า ${getGroupLabel(groupKey)} แล้ว`);
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_create_scan_special_item";
      setError(getErrorMessage(code));
    } finally {
      setSaving(false);
    }
  }

  // PMN-CRITICAL(/ScanSpecialItemsPage): Delete removes the config row, not just deactivates it, so downstream quick actions disappear after reload.
  async function handleDelete(item: ScanSpecialItem): Promise<void> {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
    if (!token) {
      setError("กรุณาเข้าสู่ระบบใหม่อีกครั้ง");
      return;
    }

    setDeletingId(item.id);
    setError("");
    setSuccessMessage("");

    try {
      const resp = await fetch(apiUrl(`/scan-special-items/${item.id}`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = (await resp.json().catch(() => ({}))) as ScanSpecialItemsResponse;
      if (!resp.ok || !data.ok) {
        throw new Error(data.error ?? "failed_to_delete_scan_special_item");
      }

      setItems((prev) => prev.filter((row) => row.id !== item.id));
      setSuccessMessage(`ลบ ${item.product.name} ออกจากหมวดแล้ว`);
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_delete_scan_special_item";
      setError(getErrorMessage(code));
    } finally {
      setDeletingId(null);
    }
  }

  function renderGroupSection(title: string, groupItems: ScanSpecialItem[]): JSX.Element {
    return (
      <div className="card border rounded-4 h-100">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="text-primary mb-0">{title}</h5>
            <span className="badge bg-light text-dark">{groupItems.length} รายการ</span>
          </div>

          {groupItems.length === 0 ? (
            <div className="text-muted small">ยังไม่มีสินค้าในหมวดนี้</div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {groupItems.map((item) => {
                const isDeleting = deletingId === item.id;
                return (
                  <div
                    key={item.id}
                    className="border rounded-3 p-3 d-flex justify-content-between align-items-start gap-3"
                  >
                    <div>
                      <div className="fw-semibold">{item.product.name}</div>
                      <div className="small text-muted">{item.product.code}</div>
                      <div className="small text-muted">
                        ราคา ฿{item.product.price.toFixed(2)} · ลำดับ {item.sort_order}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => void handleDelete(item)}
                      disabled={isDeleting}
                    >
                      {isDeleting ? "กำลังลบ..." : "ลบ"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-primary py-4 py-md-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-xl-10">
            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                  <div>
                    <h2 className="text-primary mb-1">Scan Special Items</h2>
                    <div className="text-muted">
                      จัดการสินค้าจริงที่ต้องแสดงในปุ่มลัดหน้าแสกนสำหรับหมวดกระถาง ดอกไม้ และแพ็กเกจ
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => void loadItems()}>
                      รีเฟรช
                    </button>
                    <Link to="/" className="btn btn-outline-primary">
                      กลับหน้าเมนู
                    </Link>
                  </div>
                </div>

                {error && <div className="alert alert-danger rounded-3">{error}</div>}
                {successMessage && <div className="alert alert-success rounded-3">{successMessage}</div>}

                <div className="card border rounded-4 mb-4">
                  <div className="card-body">
                    <h5 className="text-primary mb-3">เพิ่มสินค้าเข้าหมวด</h5>
                    <form className="row g-3 align-items-end mb-3" onSubmit={(e) => void handleSearch(e)}>
                      <div className="col-12 col-md-5">
                        <label className="form-label">ค้นหาสินค้า</label>
                        <input
                          type="text"
                          className="form-control"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          placeholder="ค้นหาจากรหัสหรือชื่อสินค้า"
                        />
                      </div>
                      <div className="col-12 col-md-3">
                        <label className="form-label">หมวด</label>
                        <select
                          className="form-select"
                          value={groupKey}
                          onChange={(e) => setGroupKey(e.target.value as GroupKey)}
                        >
                          <option value="POT">กระถาง</option>
                          <option value="FLOWER">ดอกไม้</option>
                          <option value="PACKAGE">แพ็กเกจ</option>
                        </select>
                      </div>
                      <div className="col-12 col-md-2">
                        <label className="form-label">ลำดับ</label>
                        <input
                          type="number"
                          className="form-control"
                          value={sortOrder}
                          onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
                          min={0}
                          step={1}
                        />
                      </div>
                      <div className="col-12 col-md-2">
                        <button type="submit" className="btn btn-primary w-100" disabled={searching}>
                          {searching ? "กำลังค้นหา..." : "ค้นหา"}
                        </button>
                      </div>
                    </form>

                    <div className="d-flex flex-column gap-2">
                      {searchResults.length === 0 ? (
                        <div className="small text-muted">ค้นหาสินค้าแล้วรายการจะขึ้นที่นี่</div>
                      ) : (
                        searchResults.map((product) => (
                          <div
                            key={product.code}
                            className="border rounded-3 p-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3"
                          >
                            <div>
                              <div className="fw-semibold">{product.name}</div>
                              <div className="small text-muted">{product.code}</div>
                              <div className="small text-muted">ราคา ฿{product.price.toFixed(2)}</div>
                            </div>
                            <button
                              type="button"
                              className="btn btn-outline-primary"
                              onClick={() => void handleAdd(product)}
                              disabled={saving}
                            >
                              {saving ? "กำลังเพิ่ม..." : `เพิ่มเข้า${getGroupLabel(groupKey)}`}
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                <div className="row g-4">
                  <div className="col-12 col-lg-4">
                    {loading ? (
                      <div className="card border rounded-4">
                        <div className="card-body text-muted">กำลังโหลดรายการกระถาง...</div>
                      </div>
                    ) : (
                      renderGroupSection("กระถาง", grouped.POT)
                    )}
                  </div>
                  <div className="col-12 col-lg-4">
                    {loading ? (
                      <div className="card border rounded-4">
                        <div className="card-body text-muted">กำลังโหลดรายการดอกไม้...</div>
                      </div>
                    ) : (
                      renderGroupSection("ดอกไม้", grouped.FLOWER)
                    )}
                  </div>
                  <div className="col-12 col-lg-4">
                    {loading ? (
                      <div className="card border rounded-4">
                        <div className="card-body text-muted">กำลังโหลดรายการแพ็กเกจ...</div>
                      </div>
                    ) : (
                      renderGroupSection("แพ็กเกจ", grouped.PACKAGE)
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
